import {
  normalizeHexColor,
  relativeLuminance,
  resolvePartyBrandingTheme,
} from "./party-branding";
import type { TvDisplayBranding } from "./tv-mode";

export interface TvStageColors {
  /** Page background. */
  canvas: string;
  /** Panels and rows. */
  surface: string;
  /** Raised cards and the podium blocks. */
  raised: string;
  /** Color of the stage light. */
  key: string;
  /** Kickers and small labels on the dark stage. */
  accent: string;
}

export const TV_STAGE_DEFAULT_COLORS: TvStageColors = {
  canvas: "#0B0A09",
  surface: "#15120E",
  raised: "#1F1A14",
  key: "#E8C96A",
  accent: "#E8C96A",
};

/** Below this the brand color disappears into the dark stage. */
const DIM_KEY_LUMINANCE = 0.03;
const DIM_KEY_LIFT = 0.35;

function towardWhite(hex: string, amount: number): string {
  const channels = [1, 3, 5].map((start) => Number.parseInt(hex.slice(start, start + 2), 16));
  return `#${channels
    .map((channel) => Math.round(channel + (255 - channel) * amount).toString(16).padStart(2, "0"))
    .join("")
    .toUpperCase()}`;
}

/** Stage colors for the TV screen, from the party brand when it has one. */
export function tvStageColors(branding: TvDisplayBranding | null): TvStageColors {
  if (!branding) return TV_STAGE_DEFAULT_COLORS;
  const primary = normalizeHexColor(branding.primaryColor);
  const accent = normalizeHexColor(branding.accentColor);
  if (!primary || !accent) return TV_STAGE_DEFAULT_COLORS;
  const theme = resolvePartyBrandingTheme({ primaryColor: primary, accentColor: accent });
  return {
    canvas: theme.canvas,
    surface: theme.surface,
    raised: theme.surfaceRaised,
    key: relativeLuminance(primary) < DIM_KEY_LUMINANCE ? towardWhite(primary, DIM_KEY_LIFT) : primary,
    accent: theme.accentOnDark,
  };
}

export function tvStageCssVariables(colors: TvStageColors): Record<string, string> {
  return {
    "--tv-canvas": colors.canvas,
    "--tv-surface": colors.surface,
    "--tv-raised": colors.raised,
    "--tv-key": colors.key,
    "--tv-accent": colors.accent,
  };
}
